import { useQueries, useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { API } from 'network/api';

type ResponseType = {
  paidAt: string;
  isHoldingCash: boolean;
  invoiceId: string;
};

const getInvoiceHoldingCashParams = () => {
  return {
    page: 0,
    size: 10,
    sort: 'paidAt,desc',
    projection: 'collection-payment-season-list-dto',
    'status.in': 'RECEIVED,PAID',
  };
};

const fetcher = async (invoiceId: string, params?: Record<string, unknown>) => {
  const allParams = {
    ...getInvoiceHoldingCashParams(),
    'invoiceId.equals': invoiceId,
    ...params,
  };
  const res = await API.get('/collection', {
    params: allParams,
  });

  return res;
};

const useInvoiceHoldingCashQuery = (
  invoiceId: string,
  params?: Record<string, unknown>,
) => {
  return useQuery({
    queryKey: ['invoice-collection', { invoiceId }, params],
    queryFn: () => fetcher(invoiceId, params),
    select: (res) => ({
      paidAt: res?.data?.[0]?.paidAt,
      isHoldingCash: res?.data?.[0]?.status === 'RECEIVED',
    }),
    enabled: !!invoiceId,
    staleTime: 10000,
  });
};

export const useInvoiceHoldingCashBatchQuery = (
  invoiceIds: string[],
  params?: Record<string, unknown>,
) => {
  const combinedQueries = useQueries({
    queries: invoiceIds.map((invoiceId) => {
      return {
        queryKey: ['invoice-collection', { invoiceId }, params],
        queryFn: () => fetcher(invoiceId, params),
        select: (res: AxiosResponse) => ({
          paidAt: res?.data?.[0]?.paidAt,
          isHoldingCash: res?.data?.[0]?.status === 'RECEIVED',
          invoiceId,
        }),
        enabled: !!invoiceId,
        staleTime: 30000,
      };
    }),
    combine: (results) => {
      return results.reduce(
        (acc, curr) => {
          if (!curr?.data?.invoiceId) return acc;
          acc[curr.data.invoiceId] = curr?.data;
          return acc;
        },
        {} as Record<string, ResponseType>,
      );
    },
  });

  return combinedQueries;
};

export default useInvoiceHoldingCashQuery;

export const useInvoicesHoldingCashQuery = (invoiceIds: string[]) => {
  const query = useQuery({
    queryKey: ['invoices-holding-cash', invoiceIds],
    queryFn: async () => {
      const res = await API.get('/collection', {
        params: {
          'invoiceId.in': invoiceIds.join(','),
          ...getInvoiceHoldingCashParams(), // Assuming all invoices have the same params
        },
      });

      return res;
    },
    enabled: invoiceIds?.length > 0,
    staleTime: 30000,
    select: (res) => {
      return res.data.reduce((acc: Record<string, ResponseType>, curr: any) => {
        if (acc[curr?.invoiceNumber]) return acc; // Avoid duplicates
        acc[curr.invoiceNumber] = {
          paidAt: curr?.paidAt,
          isHoldingCash: curr?.status === 'RECEIVED',
          invoiceId: curr?.invoiceNumber,
        };
        return acc;
      }, {});
    },
  });
  return query;
};
